import React, { ReactNode } from 'react'
import { EventFilterConditionEdit, EventFilterEdit } from './EventFilters'
import { ConditionRow } from './ConditionRow'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { Button } from '../../../components/Button/Button'
import { Accordion, Card, Col, Container, Form, Row, useAccordionButton } from 'react-bootstrap'
import { Tooltip } from '../../../components/Tooltip/Tooltip'

export type EventFilterRowProps = {
  filterId: string
  title: string
  description?: ReactNode | string
  value: EventFilterEdit | null
  defaultValue: () => EventFilterEdit | null
  save: (filterEdit: EventFilterEdit | null) => void
  disabled?: boolean
}

export const EventFilterRow: React.FC<EventFilterRowProps> = ({ filterId, title, description, value, defaultValue, save, disabled }) => {
  const handleToggleEventFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      save(defaultValue() ?? { conditions: [] })
    } else {
      save(null)
    }
  }

  const saveCondition = (index: number, condition: EventFilterConditionEdit) => {
    save({ id: value?.id, conditions: value?.conditions ? value.conditions.map((v, idx) => (idx !== index ? v : condition)) : [condition] })
  }
  const removeCondition = (index: number) => {
    save({ id: value?.id, conditions: value?.conditions ? value.conditions.filter((v, idx) => idx !== index) : [] })
  }
  const addCondition = () => {
    const newConditions = value?.conditions ?? []
    newConditions.push({ id: null, type: null, operator: null, value: null, enforced: false })
    save({ id: value?.id, conditions: newConditions })
  }
  const switchDisabled = disabled || (value?.conditions?.some((c) => c.enforced) ?? false)
  const FilterToggle = ({ eventKey }: { eventKey: string }) => {
    const decoratedHandleToggleEventFilter = useAccordionButton(eventKey, handleToggleEventFilter)
    return (
      <Row className='d-flex gap-3 justify-content-end'>
        <Col xs='auto'>
          <div className='d-flex align-items-center gap-2'>
            <Form.Switch checked={!!value} onChange={(e) => decoratedHandleToggleEventFilter(e)} label={title} disabled={switchDisabled}></Form.Switch>
            {switchDisabled && (
              <Tooltip className='zocks-text-gray-500' placement='right' title='The filter can not be switched off because it contains enforced conditions.'>
                <i className='bi bi-info-circle' />
              </Tooltip>
            )}
          </div>
        </Col>
        <Col className='text-end'>{description}</Col>
      </Row>
    )
  }

  return (
    <Accordion activeKey={value ? '0' : undefined}>
      <Card>
        <Card.Header>
          <FilterToggle eventKey='0' />
        </Card.Header>
        <Accordion.Collapse eventKey='0'>
          <Card.Body>
            <Container className='d-flex flex-column gap-2 p-0'>
              {!!value?.conditions.length && (
                <Row className='mb-2'>
                  <Col>
                    <div className='zocks-text-gray-900 lead-small'>Conditions</div>
                  </Col>
                </Row>
              )}
              {value?.conditions.map((condition, index) => (
                <ConditionRow
                  conditionId={`${filterId}_${index}`}
                  key={index}
                  condition={condition}
                  save={(condition) => {
                    saveCondition(index, condition)
                  }}
                  remove={() => {
                    removeCondition(index)
                  }}
                />
              ))}
              <Row className='mt-3'>
                <Col>
                  <ScopedBlock
                    scope={{
                      scope: 'room:list',
                      superScope: ['room:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Button variant='primary' type='button' onClick={addCondition} disabled={disabled}>
                      <i className='bi bi-plus' />
                      Add condition
                    </Button>
                  </ScopedBlock>
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  )
}
